/**
 * Les chapitres d'un parcours dans l'éditeur (#75).
 *
 * Un chapitre est attaché à une POSITION de la liste — « à partir de la 3ᵉ
 * étape » —, pas à un module : réordonner les modules re-tranche la liste sans
 * emmener un titre avec le module qu'on déplace, et un chapitre reste toujours
 * une tranche contiguë, dans l'ordre du parcours (ce que l'API exige). Il court
 * jusqu'au chapitre suivant ; les étapes d'avant le premier n'en ont pas.
 */
export type PathChapter = { start: number, title: string }

/** Longueur maximale d'un titre : miroir de `MAX_SECTION_TITLE` côté API (`module-sections.ts`). */
export const MAX_CHAPTER_TITLE = 120

/** Commence un chapitre à cette position (une seule fois par position). */
export function addChapter(chapters: PathChapter[], start: number, title: string): PathChapter[] {
  if (chapters.some(chapter => chapter.start === start)) return chapters
  return [...chapters, { start, title }].sort((a, b) => a.start - b.start)
}

export function renameChapter(chapters: PathChapter[], start: number, title: string): PathChapter[] {
  return chapters.map(chapter => (chapter.start === start ? { ...chapter, title } : chapter))
}

/** Retire un chapitre : ses étapes rejoignent le chapitre précédent (ou n'en ont plus). */
export function removeChapter(chapters: PathChapter[], start: number): PathChapter[] {
  return chapters.filter(chapter => chapter.start !== start)
}

/**
 * Chapitres après le retrait de l'étape `removed` (la liste compte désormais
 * `length` étapes) : ceux d'après remontent d'un cran, et un chapitre qui
 * perd sa dernière étape disparaît — l'API refuserait un chapitre vide.
 */
export function chaptersAfterRemoval(chapters: PathChapter[], removed: number, length: number): PathChapter[] {
  const shifted = chapters.map(chapter => ({ ...chapter, start: chapter.start > removed ? chapter.start - 1 : chapter.start }))
  // Deux chapitres à la même position : le premier n'a plus d'étape, il cède la place.
  return shifted.filter((chapter, i) => chapter.start < length && shifted[i + 1]?.start !== chapter.start)
}

/** Indice du chapitre qui contient cette position, ou null avant le premier. */
export function sectionIndexAt(chapters: PathChapter[], position: number): number | null {
  let index: number | null = null
  chapters.forEach((chapter, i) => {
    if (chapter.start <= position) index = i
  })
  return index
}

/** La liste découpée pour l'affichage : chaque segment, son chapitre (ou aucun) et ses étapes. */
export function chapterSegments<T>(steps: T[], chapters: PathChapter[]): { chapter: PathChapter | null, start: number, steps: T[] }[] {
  const starts = [0, ...chapters.map(chapter => chapter.start).filter(start => start > 0)]
  return [...new Set(starts)].map((start, i, all) => ({
    chapter: chapters.find(chapter => chapter.start === start) ?? null,
    start,
    steps: steps.slice(start, all[i + 1] ?? steps.length),
  })).filter(segment => segment.steps.length > 0)
}

/** Les chapitres d'un parcours enregistré : un chapitre commence là où le `sectionId` change. */
export function chaptersFrom(
  modules: ReadonlyArray<{ sectionId?: string | null }>,
  sections: ReadonlyArray<{ id: string, title: string }>,
): PathChapter[] {
  const titles = new Map(sections.map(section => [section.id, section.title]))
  return modules.flatMap((module, i) => {
    const id = module.sectionId
    const title = id ? titles.get(id) : undefined
    return title != null && modules[i - 1]?.sectionId !== id ? [{ start: i, title }] : []
  })
}
